import { useNavigation } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ChevronRight, Trash2 } from "@tamagui/lucide-icons";
import { uuid } from "expo-modules-core";
import { observer } from "mobx-react-lite";
import React from "react";
import { Text, XStack, YStack } from "tamagui";
import type { SortableItemWithId } from "../../core/components/SortableList";
import { useStores } from "../../core/stores";
import { Permission } from "../../permissions/types";
import { Body } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { Group } from "../../ui/Group";
import { PopoverConfirm } from "../../ui/PopoverConfirm";
import { Title } from "../../ui/Title";
import type {
  BusinessPlanModel,
  BusinessPlanSection,
} from "./BusinessPlanModel";
import BusinessPlanScreen from "./BusinessPlanScreen";
import { BusinessPlanSectionUI } from "./tabs/BusinessPlanSectionUI";
import { BusinessSortableList } from "./ui/BusinessSortableList";

type Props = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  "BusinessSection"
>;

const SectionListAdmin = observer(
  ({
    renderItem,
    businessPlan,
  }: {
    renderItem: (section: BusinessPlanSection) => React.JSX.Element;
    businessPlan: BusinessPlanModel;
  }) => {
    const renderItemWithSortable = React.useCallback(
      (item: SortableItemWithId<BusinessPlanSection>) => {
        const section = item.item;

        return (
          <XStack width="100%" alignItems={"center"}>
            {renderItem(section)}

            <XStack position={"absolute"} right={0} gap={"$medium"}>
              {section.isDeleted && <Body>Deleted</Body>}
              {!section.isVisible && <Body>Not visible</Body>}

              <PopoverConfirm
                placement="top-start"
                onConfirm={() => {
                  businessPlan.deleteBusinessSection(section.uid);
                }}
              >
                <Trash2 />
              </PopoverConfirm>
            </XStack>
          </XStack>
        );
      },
      [renderItem, businessPlan],
    );

    const onSort = React.useCallback(
      (items: BusinessPlanSection[]) => {
        businessPlan.updateSectionOrder(items);
      },
      [businessPlan],
    );

    const onAdd = React.useCallback(
      (sectionTitle: string) => {
        businessPlan.addSection({
          uid: uuid.v4(),
          title: sectionTitle,
          emoji: null,
          isDeleted: false,
          isVisible: true,
        });
      },
      [businessPlan],
    );

    return (
      <BusinessSortableList<BusinessPlanSection>
        itemDescription={"Section"}
        items={businessPlan?.data.sections}
        renderItem={renderItemWithSortable}
        onSort={onSort}
        onAdd={onAdd}
      />
    );
  },
);

const SectionList = observer(
  ({ businessPlan }: { businessPlan: BusinessPlanModel }) => {
    const { navigate } = useNavigation();

    const isAdmin = businessPlan.checkPermission(Permission.CanEditSections);

    const renderItem = React.useCallback(
      (section: BusinessPlanSection) => {
        return (
          <XStack
            width={"100%"}
            justifyContent="space-between"
            alignItems={"center"}
            flex={1}
            key={section.uid}
            cursor="pointer"
            onPress={() => {
              navigate("BusinessSection", {
                businessUid: businessPlan.uid,
                sectionUid: section.uid,
              });
            }}
          >
            <XStack gap={"$medium"} flex={1}>
              {section.emoji && (
                <Text lineHeight={24} fontSize={24}>
                  {section.emoji}
                </Text>
              )}
              {section.title}
            </XStack>

            <ChevronRight />
          </XStack>
        );
      },
      [businessPlan, navigate],
    );

    return (
      <Box>
        <YStack gap={"$medium"}>
          <Title>Info</Title>

          {!isAdmin && (
            <Group>{businessPlan?.activeSections.map(renderItem)}</Group>
          )}

          {isAdmin && (
            <SectionListAdmin
              renderItem={renderItem}
              businessPlan={businessPlan}
            />
          )}
        </YStack>
      </Box>
    );
  },
);

export const BusinessPlanSectionScreen = observer((props: Props) => {
  const { businessPlansStore } = useStores();

  const businessPlan = businessPlansStore.byId.get(
    props.route.params.businessUid,
  );

  const hasMultipleSections: boolean =
    (businessPlan?.activeSections.length ?? 0) > 1 ||
    (businessPlan?.checkPermission(Permission.CanEditSections) ?? false);

  // Show section at once if only one
  const showSectionUid =
    props.route.params.sectionUid ??
    (!hasMultipleSections ? businessPlan?.activeSections[0].uid : undefined);

  const section = showSectionUid
    ? businessPlan?.getSection(showSectionUid)
    : undefined;

  let content: JSX.Element | undefined;

  if (section && showSectionUid) {
    content = (
      <BusinessPlanSectionUI
        section={section}
        businessPlanUid={props.route.params.businessUid}
      />
    );
  } else if (businessPlan) {
    content = <SectionList businessPlan={businessPlan} />;
  }

  return <BusinessPlanScreen {...props}>{content}</BusinessPlanScreen>;
});

export default BusinessPlanSectionScreen;
